import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DASH } from "@/constants/testIds";

const SIGNS = ["Ari","Tau","Gem","Can","Leo","Vir","Lib","Sco","Sag","Cap","Aqu","Pis"];
const SIGN_FULL = ["Aries","Taurus","Gemini","Cancer","Leo","Virgo","Libra","Scorpio","Sagittarius","Capricorn","Aquarius","Pisces"];
const P_SHORT = { Sun:"Su", Moon:"Mo", Mars:"Ma", Mercury:"Me", Jupiter:"Ju", Venus:"Ve", Saturn:"Sa", Rahu:"Ra", Ketu:"Ke" };

// ---------- North-Indian: houses fixed, signs rotate ----------
function NorthChart({ chart, ascSign, title }) {
  const houseSigns = Array.from({ length: 12 }, (_, i) => (ascSign + i) % 12);
  const housePlanets = Array.from({ length: 12 }, () => []);
  Object.entries(chart).forEach(([k, v]) => {
    if (k === "ASC") return;
    const h = ((v - ascSign) % 12 + 12) % 12;
    housePlanets[h].push(P_SHORT[k] || k);
  });
  return (
    <div className="border border-[#D4AF37]/30 aspect-square relative bg-[#0F1115]/60">
      <div className="absolute inset-0 grid grid-cols-4 gap-px p-px">
        {houseSigns.map((sign, h) => (
          <div key={`nh-${h + 1}`}
               className="border border-[#2A2D35] p-2 text-xs bg-[#181A1F]/60 flex flex-col relative"
               data-testid={`house-${h + 1}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[#6E6B64]">{h + 1}</span>
              <span className="text-[#D4AF37] text-[10px] uppercase tracking-wider">{SIGNS[sign]}</span>
            </div>
            <div className="mt-auto flex flex-wrap gap-1">
              {housePlanets[h].map((p) => (
                <span key={p} className="text-[#E6E1D6] font-mono text-xs px-1 bg-[#0F1115] border border-[#D4AF37]/20 rounded-sm">
                  {p}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      {title && (
        <div className="absolute -top-3 left-3 bg-[#0F1115] px-2 text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">
          {title}
        </div>
      )}
    </div>
  );
}

// ---------- South-Indian: signs fixed on the frame, ascendant marked with a diagonal ----------
// Layout (row, col) 0-indexed, 4x4 grid — outer ring is signs, middle 2x2 is empty:
// (0,0)=Pisces(11)  (0,1)=Aries(0)   (0,2)=Taurus(1)  (0,3)=Gemini(2)
// (1,0)=Aquarius(10) —                —                (1,3)=Cancer(3)
// (2,0)=Capricorn(9) —                —                (2,3)=Leo(4)
// (3,0)=Sagittar(8) (3,1)=Scorpio(7) (3,2)=Libra(6)   (3,3)=Virgo(5)
const SOUTH_LAYOUT = [
  // {sign, row, col}
  { sign: 11, r: 0, c: 0 }, { sign: 0, r: 0, c: 1 }, { sign: 1, r: 0, c: 2 }, { sign: 2, r: 0, c: 3 },
  { sign: 10, r: 1, c: 0 }, { sign: 3, r: 1, c: 3 },
  { sign: 9, r: 2, c: 0 }, { sign: 4, r: 2, c: 3 },
  { sign: 8, r: 3, c: 0 }, { sign: 7, r: 3, c: 1 }, { sign: 6, r: 3, c: 2 }, { sign: 5, r: 3, c: 3 },
];

function SouthChart({ chart, ascSign, title }) {
  const signPlanets = Array.from({ length: 12 }, () => []);
  Object.entries(chart).forEach(([k, v]) => {
    if (k === "ASC") return;
    signPlanets[v].push(P_SHORT[k] || k);
  });

  const cellAt = (r, c) => SOUTH_LAYOUT.find(x => x.r === r && x.c === c);

  return (
    <div className="border border-[#D4AF37]/30 aspect-square relative bg-[#0F1115]/60">
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-4 gap-px p-px">
        {[0, 1, 2, 3].map((r) => [0, 1, 2, 3].map((c) => {
          const cell = cellAt(r, c);
          if (!cell) {
            // Center cells — merged visually via empty background
            const isCenter = (r === 1 || r === 2) && (c === 1 || c === 2);
            if (isCenter && r === 1 && c === 1) {
              return (
                <div key={`center`} className="col-span-2 row-span-2 flex items-center justify-center border border-[#2A2D35] bg-[#0F1115]/40">
                  <div className="text-center">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">South-Indian</div>
                    <div className="text-[10px] font-mono text-[#6E6B64] mt-1">D1 · Rasi</div>
                  </div>
                </div>
              );
            }
            return null;
          }
          const isAsc = cell.sign === ascSign;
          return (
            <div key={`s-${cell.sign}`}
                 className="border p-2 text-xs bg-[#181A1F]/60 flex flex-col relative overflow-hidden"
                 style={{ borderColor: isAsc ? "#D4AF37" : "#2A2D35" }}
                 data-testid={`south-sign-${cell.sign}`}>
              {/* Ascendant diagonal marker (top-left corner triangle) */}
              {isAsc && (
                <>
                  <div className="absolute top-0 left-0 w-4 h-4 pointer-events-none"
                       style={{ background: "linear-gradient(135deg, #D4AF37 0%, #D4AF37 50%, transparent 50%)" }} />
                  <div className="absolute top-0.5 left-0.5 text-[8px] font-bold text-[#0F1115] leading-none">Asc</div>
                </>
              )}
              <div className="flex items-center justify-between">
                <span className={`text-[10px] uppercase tracking-wider ${isAsc ? "text-[#D4AF37]" : "text-[#6E6B64]"}`}>
                  {SIGNS[cell.sign]}
                </span>
              </div>
              <div className="mt-auto flex flex-wrap gap-1">
                {signPlanets[cell.sign].map((p) => (
                  <span key={p}
                        className="text-[#E6E1D6] font-mono text-xs px-1 bg-[#0F1115] border border-[#D4AF37]/20 rounded-sm">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          );
        }))}
      </div>
      {title && (
        <div className="absolute -top-3 left-3 bg-[#0F1115] px-2 text-[10px] font-mono uppercase tracking-widest text-[#D4AF37]">
          {title}
        </div>
      )}
    </div>
  );
}

export default function ChartsView({ chart }) {
  const [varga, setVarga] = useState("D1");
  const [style, setStyle] = useState("south"); // 'north' | 'south'
  if (!chart) return null;

  const ascSign = chart.ascendant.asc_sign;
  const current = varga === "D1"
    ? Object.fromEntries(Object.entries(chart.planets).map(([k, v]) => [k, v.sign]))
    : chart.vargas[varga];
  const currentAsc = varga === "D1" ? ascSign : chart.vargas[varga]?.ASC;

  const ChartCmp = style === "south" ? SouthChart : NorthChart;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#A19E95] font-mono">Ascendant</div>
          <div className="font-heading text-3xl text-[#E6E1D6]">
            {SIGN_FULL[ascSign]}{" "}
            <span className="text-[#D4AF37] font-mono text-lg">{chart.ascendant.asc_deg_in_sign.toFixed(2)}°</span>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="w-44">
            <div className="text-[10px] uppercase tracking-widest text-[#6E6B64] font-mono mb-1">Style</div>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger data-testid="chart-style-select"
                             className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
                <SelectItem value="south">South Indian</SelectItem>
                <SelectItem value="north">North Indian</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <div className="text-[10px] uppercase tracking-widest text-[#6E6B64] font-mono mb-1">Varga</div>
            <Select value={varga} onValueChange={setVarga}>
              <SelectTrigger data-testid={DASH.vargaSelect}
                             className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6] max-h-72">
                {["D1","D2","D3","D7","D9","D10","D12","D16","D20","D24","D27","D30","D40","D45","D60"].map(v => (
                  <SelectItem key={v} value={v}>{v} — {vargaLabel(v)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCmp chart={current} ascSign={currentAsc} title={`${varga} — ${vargaLabel(varga)}`} />
        <div className="border border-[#2A2D35] p-6">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono">Natal (D1) Planetary Positions</div>
          <div className="mt-4 font-mono text-xs overflow-x-auto">
            <table className="w-full">
              <thead className="text-[#6E6B64]">
                <tr>
                  <th className="text-left py-2">Planet</th>
                  <th className="text-left">Sign</th>
                  <th className="text-right">Deg</th>
                  <th className="text-left pl-3">Nakshatra</th>
                  <th className="text-center">H</th>
                  <th className="text-center">R</th>
                  <th className="text-center">C</th>
                </tr>
              </thead>
              <tbody className="text-[#E6E1D6]">
                {Object.entries(chart.planets).map(([k, v]) => (
                  <tr key={k} className="border-t border-[#2A2D35]" data-testid={`row-planet-${k}`}>
                    <td className="py-1.5">{k}</td>
                    <td>{v.sign_name}</td>
                    <td className="text-right">{v.deg_in_sign.toFixed(2)}</td>
                    <td className="pl-3">{v.nakshatra} <span className="text-[#6E6B64]">/{v.pada}</span></td>
                    <td className="text-center">{v.house}</td>
                    <td className="text-center text-[#C15C3D]">{v.retrograde ? "R" : ""}</td>
                    <td className="text-center text-[#A8896C]">{v.combust ? "C" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function vargaLabel(v) {
  return { D1:"Rasi", D2:"Hora", D3:"Drekkana", D7:"Saptamsha", D9:"Navamsha", D10:"Dashamsha",
           D12:"Dwadashamsha", D16:"Shodashamsha", D20:"Vimshamsha", D24:"Chaturvimshamsha",
           D27:"Bhamsha", D30:"Trimsamsha", D40:"Khavedamsha", D45:"Akshavedamsha", D60:"Shashtiamsha" }[v] || v;
}
